// Apolo IDE — PlanSuggestionBar (sprint plan-suggestions).
//
// Faixa de chips no topo do canvas com os próximos passos sugeridos.
// Heurística (grátis) + chip "✨ mais ideias" (LLM sob demanda). Dismissable.

import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';
import { usePlanSuggestions } from '../../hooks/usePlanSuggestions.js';

export default function PlanSuggestionBar({ plan, blocks, onApply, onDismiss }) {
  const { suggestions, fetchAiSuggestions, loadingAi, error } = usePlanSuggestions(plan, blocks);

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      role="toolbar"
      aria-label="Próximos passos sugeridos"
      className="flex shrink-0 items-center gap-2 overflow-x-auto border-b border-apolo-borderSubtle bg-apolo-surface1/60 px-3 py-2 backdrop-blur-md"
      style={{ scrollbarWidth: 'none' }}
    >
      <span className="inline-flex shrink-0 items-center gap-1 pr-1 text-[10px] font-semibold uppercase tracking-wide text-apolo-textTertiary">
        <Icons.Sparkles size={12} className="text-apolo-plan-accent" aria-hidden="true" />
        Próximos passos
      </span>

      {suggestions.length === 0 && !loadingAi ? (
        <span className="shrink-0 text-xs text-apolo-textTertiary">Plano coberto nas 6 fases ✓</span>
      ) : null}

      {suggestions.map((s) => {
        const Icon = Icons[s.icon] || Icons.Plus;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onApply(s)}
            title={s.reason}
            aria-label={`Adicionar: ${s.label}. ${s.reason}`}
            className={`group inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition active:scale-[0.97] ${
              s.ai
                ? 'border-apolo-plan-accentBorder bg-apolo-plan-accentMuted text-apolo-plan-accent hover:brightness-110'
                : 'border-apolo-borderSubtle bg-apolo-bg text-apolo-textPrimary hover:border-apolo-plan-accentBorder hover:bg-apolo-plan-accentMuted'
            }`}
          >
            <Icon size={13} aria-hidden="true" className={s.ai ? '' : 'text-apolo-plan-accent'} />
            {s.id !== 'scaffold' && !s.ai ? <span className="text-apolo-textTertiary group-hover:text-apolo-plan-accent">+</span> : null}
            {s.label}
          </button>
        );
      })}

      {/* Chip fixo: mais ideias via LLM */}
      <button
        type="button"
        onClick={fetchAiSuggestions}
        disabled={loadingAi}
        title="Pede ao copiloto 3 próximos passos específicos ao seu canvas"
        aria-label="Mais ideias com IA"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-dashed border-apolo-plan-accentBorder px-3 py-1.5 text-xs font-medium text-apolo-plan-accent transition hover:bg-apolo-plan-accentMuted active:scale-[0.97] disabled:opacity-60"
      >
        {loadingAi ? <Icons.Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Icons.Wand2 size={13} aria-hidden="true" />}
        mais ideias
      </button>

      {error ? <span className="shrink-0 text-[11px] text-apolo-error">{error}</span> : null}

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Esconder sugestões"
        className="ml-auto inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
      >
        <Icons.X size={13} />
      </button>
    </motion.div>
  );
}
